import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, Camera, Loader2, X, CheckCircle2, AlertTriangle, ShieldCheck, ShieldAlert,
  Package, MapPin, Clock, ScanLine, FileText,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../lib/api.js'
import { generateThumbnail } from '../lib/thumbnail.js'
import { uploadFile } from '../lib/uploadDirect.js'
import {
  profileImage, imageThumbSrc, indexTypes, typeFull, relabelBranch, fmtDateTH, checkDueState,
} from '../lib/asset.js'
import { verifyScan, checkPath } from '../lib/assetScan.js'
import QrScanner from '../components/asset/QrScanner.jsx'

const MAX_PHOTOS = 4

function blobToDataURL(blob) {
  return new Promise((resolve) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = () => resolve('')
    r.readAsDataURL(blob)
  })
}

/* Fast check screen — the landing spot for a scanned QR.
 *
 * The whole point is that a check is ONE screen: confirm you are holding the
 * right machine, photograph it as proof it is still in the clinic, save, scan
 * the next one. It writes the same asset.check[] entry as the card on the detail
 * page — the photo here is evidence-of-existence, NOT the asset's profile image
 * (that one lives in asset.image[] and is managed on the detail page). */
export default function AssetCheck() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [sp] = useSearchParams()
  const scannedUid = sp.get('u') || ''

  const [asset, setAsset] = useState(null)
  const [types, setTypes] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [picked, setPicked] = useState([]) // { file, preview }
  const [remark, setRemark] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState('')
  const [done, setDone] = useState(false)
  const [scanning, setScanning] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    cachedGet('/api/asset-types').then((r) => setTypes(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  // Reload AND reset on every id change — scanning the next asset reuses this
  // same route, so leftover photos from the previous one would be a real hazard.
  useEffect(() => {
    let alive = true
    setLoading(true); setErr(''); setSaveErr(''); setDone(false); setRemark('')
    setPicked((p) => { p.forEach((x) => x.preview && URL.revokeObjectURL(x.preview)); return [] })
    apiGet(`/api/assets/${id}`)
      .then((a) => { if (alive) setAsset(a) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [id])

  const { byId } = useMemo(() => indexTypes(types), [types])
  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name))
    return m
  }, [branches])

  function onPick(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    setPicked((p) => {
      const next = [...p]
      for (const f of files) {
        if (next.length >= MAX_PHOTOS) break
        next.push({ file: f, preview: URL.createObjectURL(f) })
      }
      return next
    })
  }

  function removePicked(i) {
    setPicked((p) => {
      const t = p.slice()
      const [rm] = t.splice(i, 1)
      if (rm?.preview) URL.revokeObjectURL(rm.preview)
      return t
    })
  }

  async function save() {
    if (!picked.length && !remark.trim()) { setSaveErr('ถ่ายรูปหรือกรอกหมายเหตุอย่างน้อยหนึ่งอย่าง'); return }
    setSaving(true); setSaveErr('')
    try {
      const fileObjs = []
      for (const { file } of picked) {
        // Direct to Wasabi where CORS allows, else the old proxy — uploadDirect.js.
        const obj = await uploadFile('/api/asset-upload', { asset_id: String(asset.id) }, file)
        const thumb = await generateThumbnail(file).catch(() => null)
        if (thumb) obj.thumbnail = await blobToDataURL(thumb)
        fileObjs.push(obj)
      }
      const entry = { remark: remark.trim(), file: fileObjs, created_at: Date.now() }
      const next = [...(Array.isArray(asset.check) ? asset.check : []), entry]
      await patchJson(`/api/assets/${asset.id}`, { check: next })
      picked.forEach((p) => p.preview && URL.revokeObjectURL(p.preview))
      setPicked([])
      setDone(true)
    } catch (e) {
      setSaveErr(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="px-6 py-16 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</div>
  if (err) return (
    <div className="px-6 py-16 text-center">
      <p className="text-rose-500 text-[14px] mb-4">{err}</p>
      <button onClick={() => navigate('/manager/assets')} className="text-[13.5px] text-brand-600 hover:underline">กลับไปที่ทะเบียนสินทรัพย์</button>
    </div>
  )
  if (!asset) return <div className="px-6 py-16 text-center text-slate-400">ไม่พบสินทรัพย์</div>

  const verdict = verifyScan(asset, scannedUid)
  const profile = profileImage(asset)
  const due = checkDueState(asset)

  return (
    <div className="px-4 py-5 sm:px-6 max-w-[560px] mx-auto pb-24">
      <button onClick={() => navigate(`/manager/assets/${asset.id}`)} className="flex items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-slate-800 mb-3">
        <ArrowLeft size={15} /> เปิดหน้าสินทรัพย์เต็ม
      </button>

      {/* sticker verification — a mismatch means the label is on the wrong unit */}
      {verdict === 'mismatch' && (
        <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 mb-3">
          <ShieldAlert size={17} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-[13px] text-rose-700 leading-relaxed">
            <b>สติกเกอร์ไม่ตรงกับสินทรัพย์นี้</b><br />
            รหัสใน QR ไม่ตรงกับทะเบียน — อาจเป็นสติกเกอร์ที่ลอกมาติดผิดเครื่อง หรือป้ายถูกทำซ้ำ
            ตรวจสอบ Serial กับตัวเครื่องก่อนบันทึก
          </div>
        </div>
      )}
      {verdict === 'ok' && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2 mb-3 text-[12.5px] text-emerald-700">
          <ShieldCheck size={15} className="shrink-0" /> ยืนยันจาก QR แล้ว — ตรงกับทะเบียน
        </div>
      )}

      {/* identity card — confirm you are holding the right thing */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4">
        <div className="flex items-start gap-3">
          {profile
            ? <img src={imageThumbSrc(profile)} alt="" className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200 shrink-0" />
            : <span className="w-16 h-16 rounded-xl bg-slate-100 text-slate-300 flex items-center justify-center shrink-0"><Package size={24} /></span>}
          <div className="min-w-0 flex-1">
            <h1 className="text-[17px] font-semibold text-slate-900 leading-tight">{asset.name || '(ไม่มีชื่อ)'}</h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{typeFull(byId, asset.asset_type_id)}</p>
            {(asset.brand || asset.model) && <p className="text-[13px] text-slate-500">{[asset.brand, asset.model].filter(Boolean).join(' · ')}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 pt-3 border-t border-slate-100 text-[12.5px]">
          <div><span className="text-slate-400">S/N</span> <span className="text-slate-700 tabular-nums">{asset.serial_num || '—'}</span></div>
          <div><span className="text-slate-400">รหัส</span> <span className="text-slate-700 tabular-nums">#{asset.id}</span></div>
          <div><span className="text-slate-400">สาขา</span> <span className="text-slate-700">{branchName.get(Number(asset.branch_id)) || '—'}</span></div>
          {asset.location && <div className="inline-flex items-center gap-1 text-slate-700"><MapPin size={12} className="text-slate-400" />{asset.location}</div>}
        </div>
        <div className={`flex items-center gap-1.5 mt-2.5 text-[12.5px] ${due.due ? 'text-amber-600' : 'text-slate-400'}`}>
          <Clock size={13} />
          {due.never ? 'ยังไม่เคยตรวจเช็ก' : `ตรวจล่าสุด ${fmtDateTH(due.last)}`}
        </div>
      </div>

      {done ? (
        /* ── saved: keep the round moving ─────────────────────────────────── */
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 text-center">
          <CheckCircle2 size={34} className="text-emerald-600 mx-auto mb-2" />
          <p className="text-[15px] font-semibold text-emerald-800">บันทึกการตรวจแล้ว</p>
          <p className="text-[13px] text-emerald-700/80 mt-0.5 mb-4">{fmtDateTH(Date.now())} — ยืนยันว่าสินทรัพย์นี้ยังอยู่ในคลินิก</p>
          <button
            onClick={() => setScanning(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[15px] font-semibold text-white bg-brand-600 hover:bg-brand-700"
          >
            <ScanLine size={17} /> สแกนตัวถัดไป
          </button>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={() => navigate(`/manager/assets/${asset.id}`)} className="flex-1 px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50">ดูรายละเอียด</button>
            <button onClick={() => navigate('/manager/assets')} className="flex-1 px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50">กลับทะเบียน</button>
          </div>
        </div>
      ) : (
        /* ── the check itself ─────────────────────────────────────────────── */
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><CheckCircle2 size={16} /></span>
            <h2 className="text-[14.5px] font-semibold text-slate-800">บันทึกการตรวจเช็ก</h2>
          </div>

          <button
            onClick={() => fileRef.current?.click()}
            disabled={picked.length >= MAX_PHOTOS}
            className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl text-[15px] font-semibold text-brand-700 bg-brand-50 border-2 border-dashed border-brand-300 hover:bg-brand-100 disabled:opacity-50 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
          >
            <Camera size={19} /> {picked.length ? `ถ่ายเพิ่ม (${picked.length}/${MAX_PHOTOS})` : 'ถ่ายรูปสินทรัพย์'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onPick} className="hidden" />

          {picked.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {picked.map((p, i) => (
                <div key={i} className="relative w-20 h-20">
                  <img src={p.preview} alt="" className="w-20 h-20 rounded-xl object-cover ring-1 ring-slate-200" />
                  <button onClick={() => removePicked(i)} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center hover:bg-rose-600"><X size={13} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3">
            <span className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 mb-1"><FileText size={13} /> หมายเหตุ (ถ้ามี)</span>
            <textarea
              value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
              placeholder="สภาพเครื่อง / สิ่งที่พบ…"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 resize-none"
            />
          </div>

          {saveErr && <div className="flex items-start gap-1.5 text-[12.5px] text-rose-600 mt-2"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {saveErr}</div>}

          <button
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-3 rounded-xl text-[15px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle2 size={17} />} บันทึกการตรวจ
          </button>
          <button
            onClick={() => setScanning(true)}
            className="w-full flex items-center justify-center gap-1.5 mt-2 px-4 py-2.5 rounded-xl text-[13.5px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            <ScanLine size={15} /> สแกนตัวอื่น
          </button>
        </div>
      )}

      {scanning && (
        <QrScanner
          onClose={() => setScanning(false)}
          onDetect={(p) => { setScanning(false); navigate(checkPath(p)) }}
        />
      )}
    </div>
  )
}
